import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { UiV2Scope } from '@kawai/ui';
import { AuthGuard } from './components/AuthGuard.js';
import { Layout } from './components/Layout.js';
import { RequireFeature } from './components/RequireFeature.js';
import { CustomersListPage } from './pages/CustomersListPage.js';
import { SignInPage } from './pages/SignInPage.js';
import { LegacyRedirect } from './components/LegacyRedirect.js';
import { ROUTES, ROUTE_PATHS } from './routes.js';

// S-91: 初回 bundle 削減のため、SSO 遷移直後に必ず通る SignIn / 顧客一覧（index）以外の
// ページは route 単位で遅延ロードする。分割 chunk はハッシュ付き immutable キャッシュのため
// 2 回目以降の遷移コストはない。
const CustomerDetailPage = lazy(() =>
  import('./pages/CustomerDetailPage.js').then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const CustomerAnalyticsPage = lazy(() =>
  import('./pages/CustomerAnalyticsPage.js').then((m) => ({
    default: m.CustomerAnalyticsPage,
  })),
);
const SegmentComposePage = lazy(() =>
  import('./pages/SegmentComposePage.js').then((m) => ({
    default: m.SegmentComposePage,
  })),
);
const CampaignsPage = lazy(() =>
  import('./pages/CampaignsPage.js').then((m) => ({ default: m.CampaignsPage })),
);
const ScheduledPushesPage = lazy(() =>
  import('./pages/ScheduledPushesPage.js').then((m) => ({
    default: m.ScheduledPushesPage,
  })),
);
const SurveysListPage = lazy(() =>
  import('./pages/SurveysListPage.js').then((m) => ({ default: m.SurveysListPage })),
);
const SurveyEditPage = lazy(() =>
  import('./pages/SurveyEditPage.js').then((m) => ({ default: m.SurveyEditPage })),
);
const CouponsListPage = lazy(() =>
  import('./pages/CouponsListPage.js').then((m) => ({ default: m.CouponsListPage })),
);
const CouponEditPage = lazy(() =>
  import('./pages/CouponEditPage.js').then((m) => ({ default: m.CouponEditPage })),
);
const CouponUsagesPage = lazy(() =>
  import('./pages/CouponUsagesPage.js').then((m) => ({
    default: m.CouponUsagesPage,
  })),
);
const DeliveryTreePage = lazy(() =>
  import('./pages/DeliveryTreePage.js').then((m) => ({
    default: m.DeliveryTreePage,
  })),
);
const PromoPlansListPage = lazy(() =>
  import('./pages/PromoPlansListPage.js').then((m) => ({
    default: m.PromoPlansListPage,
  })),
);
const PromoKpiPage = lazy(() =>
  import('./pages/PromoKpiPage.js').then((m) => ({ default: m.PromoKpiPage })),
);
const LotteryEventSettingsPage = lazy(() =>
  import('./pages/LotteryEventSettingsPage.js').then((m) => ({
    default: m.LotteryEventSettingsPage,
  })),
);
const LotteryCouponsPage = lazy(() =>
  import('./pages/LotteryCouponsPage.js').then((m) => ({
    default: m.LotteryCouponsPage,
  })),
);
const LotteryStatusPage = lazy(() =>
  import('./pages/LotteryStatusPage.js').then((m) => ({
    default: m.LotteryStatusPage,
  })),
);
const PromoPlanNewPage = lazy(() =>
  import('./pages/PromoPlanNewPage.js').then((m) => ({
    default: m.PromoPlanNewPage,
  })),
);
const PromoPlanEditPage = lazy(() =>
  import('./pages/PromoPlanEditPage.js').then((m) => ({
    default: m.PromoPlanEditPage,
  })),
);
const ScenarioEditPage = lazy(() =>
  import('./pages/ScenarioEditPage.js').then((m) => ({
    default: m.ScenarioEditPage,
  })),
);
const HotLeadsPage = lazy(() =>
  import('./pages/HotLeadsPage.js').then((m) => ({
    default: m.HotLeadsPage,
  })),
);
const DiagnosisPage = lazy(() =>
  import('./pages/DiagnosisPage.js').then((m) => ({
    default: m.DiagnosisPage,
  })),
);
const EngineGuidePage = lazy(() =>
  import('./pages/EngineGuidePage.js').then((m) => ({
    default: m.EngineGuidePage,
  })),
);

/**
 * S-91: 遅延ページの読込中はシェル（ヘッダ / サイドバー）を維持したまま main 領域のみ
 * 繋ぎ表示する pathless layout route。fallback は AuthGuard の loading 表示と同じ文言。
 */
function PageSuspense() {
  return (
    <Suspense
      fallback={
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center py-16 text-sm text-muted-foreground"
        >
          読み込み中…
        </div>
      }
    >
      <Outlet />
    </Suspense>
  );
}

/**
 * S-132 段階 1: 書体・字間・行間（`.ui-v2`）を全ページへ。S-138: 同じスコープで @kawai/ui の
 * Button / Input / Select / Textarea / Modal が既定で v2（枠線なし）になる（`UiV2Scope` の context）。
 * 左・上部メニュー（AppShell）は対象外。
 */
function V2Scope() {
  return (
    <UiV2Scope>
      <Outlet />
    </UiV2Scope>
  );
}

/**
 * tenant-promo app の root route（LE-00 / promo feature）。
 *
 * - `/signin`              : SignInPage
 * - `/`                     : 顧客一覧（AuthGuard + RequireFeature('promo') + Layout）
 * - `/customers/:uid`       : 顧客詳細
 * - `/segments/new`         : セグメント配信作成
 * - `/campaigns`            : 配信履歴
 * - その他                  : `/` へ redirect
 */
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path={ROUTES.signIn} element={<SignInPage />} />

        <Route
          element={
            <AuthGuard>
              <RequireFeature feature="promo">
                <Layout />
              </RequireFeature>
            </AuthGuard>
          }
        >
          <Route element={<V2Scope />}>
            <Route index element={<CustomersListPage />} />
            <Route element={<PageSuspense />}>
              <Route path={ROUTE_PATHS.customerDetail} element={<CustomerDetailPage />} />
              {/* M-161: 分析サマリー（顧客在庫の 9 セクション / M-159）は独立ページ。 */}
              <Route path={ROUTE_PATHS.analytics} element={<CustomerAnalyticsPage />} />
              <Route path={ROUTE_PATHS.segmentCompose} element={<SegmentComposePage />} />
              <Route
                path={ROUTE_PATHS.scheduledPushes}
                element={<ScheduledPushesPage />}
              />
              <Route path={ROUTE_PATHS.campaigns} element={<CampaignsPage />} />
              <Route path={ROUTE_PATHS.hotLeads} element={<HotLeadsPage />} />
              {/* M-143: `/plans` は配信ツリー。旧「販促プラン」は `/kpi` へ移した。 */}
              <Route path={ROUTE_PATHS.plans} element={<DeliveryTreePage />} />
              {/* M-153: `/kpi` は商材ごとのゴール × 実測。旧「販促プラン」一覧は `/kpi/plans`。 */}
              <Route path={ROUTE_PATHS.kpi} element={<PromoKpiPage />} />
              {/* M-164: 上部タブ「イベント」配下（左メニューは Layout が /event で切り替える）。 */}
              <Route path={ROUTE_PATHS.eventSettings} element={<LotteryEventSettingsPage />} />
              <Route path={ROUTE_PATHS.eventCoupons} element={<LotteryCouponsPage />} />
              <Route path={ROUTE_PATHS.eventStatus} element={<LotteryStatusPage />} />
              {/* 初版 URL（/lottery）は抽選状況へ。 */}
              <Route
                path={ROUTE_PATHS.lottery}
                element={<Navigate to={ROUTES.eventStatus} replace />}
              />
              <Route path={ROUTE_PATHS.kpiPlans} element={<PromoPlansListPage />} />
              {/* `/kpi/new` は静的セグメントなので `:planId` より先に宣言する。 */}
              <Route path={ROUTE_PATHS.kpiPlanNew} element={<PromoPlanNewPage />} />
              <Route path={ROUTE_PATHS.kpiPlanEdit} element={<PromoPlanEditPage />} />
              {/* M-91: シナリオ配信。`/scenarios/new` を `:scenarioId` より先に。 */}
              <Route path={ROUTE_PATHS.scenarioNew} element={<ScenarioEditPage />} />
              <Route path={ROUTE_PATHS.scenarioEdit} element={<ScenarioEditPage />} />
              {/* M-143 以前の URL → 新 URL（深いパスから先に宣言）。 */}
              <Route
                path={ROUTE_PATHS.legacyScenarioNew}
                element={<LegacyRedirect kind="scenarioNew" />}
              />
              <Route
                path={ROUTE_PATHS.legacyScenarioEdit}
                element={<LegacyRedirect kind="scenarioEdit" />}
              />
              <Route
                path={ROUTE_PATHS.legacyPlanNew}
                element={<LegacyRedirect kind="planNew" />}
              />
              <Route
                path={ROUTE_PATHS.legacyPlanEdit}
                element={<LegacyRedirect kind="planEdit" />}
              />
              <Route path={ROUTE_PATHS.surveys} element={<SurveysListPage />} />
              {/* `/surveys/new` は静的セグメントなので `:surveyId` より先に宣言する。 */}
              <Route path={ROUTE_PATHS.surveyNew} element={<SurveyEditPage />} />
              <Route path={ROUTE_PATHS.surveyEdit} element={<SurveyEditPage />} />
              <Route path={ROUTE_PATHS.coupons} element={<CouponsListPage />} />
              <Route path={ROUTE_PATHS.couponNew} element={<CouponEditPage />} />
              <Route path={ROUTE_PATHS.couponEdit} element={<CouponEditPage />} />
              <Route path={ROUTE_PATHS.couponUsages} element={<CouponUsagesPage />} />
              {/* M-51: 予約特典クーポン（同じページを kind='booking_reward' で再利用）。 */}
              <Route
                path={ROUTE_PATHS.rewardCoupons}
                element={<CouponsListPage kind="booking_reward" />}
              />
              <Route
                path={ROUTE_PATHS.rewardCouponNew}
                element={<CouponEditPage kind="booking_reward" />}
              />
              <Route
                path={ROUTE_PATHS.rewardCouponEdit}
                element={<CouponEditPage kind="booking_reward" />}
              />
              <Route
                path={ROUTE_PATHS.rewardCouponUsages}
                element={<CouponUsagesPage />}
              />
              {/* M-124: 初回診断アンケート（フロー可視化 + 設定 + 配信実行）。 */}
              <Route path={ROUTE_PATHS.diagnosis} element={<DiagnosisPage />} />
              {/* 読み物（確度エンジンの説明）。商材ごとの実設定値を読んで図に反映する。 */}
              <Route path={ROUTE_PATHS.engineGuide} element={<EngineGuidePage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
